import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faRobot,
  faPaperPlane,
  faTimes,
  faTag,
  faPlane,
  faLightbulb,
  faBullhorn,
  faFolder,
  faChevronUp,
  faChevronDown,
  faUser,
  faBell,
  faComment,
  faCloudUploadAlt,
  faFilePdf,
  faFileExcel,
  faEllipsisV,
  faPlus,
  faUserPlus,
  faUserMinus,
  faSun,
  faMoon
} from "@fortawesome/free-solid-svg-icons";

// Icon mapping for categories
const faIconsMap = {
  faTag,
  faPlane,
  faLightbulb,
  faBullhorn,
  faFolder,
  faUser,
  faPaperPlane,
  faRobot,
  faUserCircle,
  faBell,
  faComment,
  faCloudUploadAlt,
  faFilePdf,
  faFileExcel,
  faEllipsisV,
  faChevronUp,
  faChevronDown,
  faSun,
  faMoon,
};

export default function BookkeeperDashboardSection() {
  // Demo data for customers
  const expenseCategories = [
    { label: "Office Supplies", icon: "faTag" },
    { label: "Travel", icon: "faPlane" },
    { label: "Utilities", icon: "faLightbulb" },
    { label: "Advertising", icon: "faBullhorn" }
  ];
  const businessClients = [
    {
      name: "Acme Inc.",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Travel", "Utilities", "Advertising"] },
        { year: "2022", expenses: ["Office Supplies", "Travel"] }
      ],
      contact: "acme@email.com"
    },
    {
      name: "J. Lee Holdings",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Utilities"] }
      ],
      contact: "lee@email.com"
    }
  ];
  const individualClients = [
    {
      name: "Smith Family Trust",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Travel"] }
      ],
      contact: "smith@email.com"
    },
    {
      name: "Jane Doe",
      taxYears: [
        { year: "2022", expenses: ["Advertising"] }
      ],
      contact: "jane@email.com"
    }
  ];
  const uploadedFiles = [
    { name: "Bank_Statement_April.pdf", type: "pdf", date: "1 hour ago", size: "2.1 MB" },
    { name: "Q2_Receipts.xlsx", type: "excel", date: "Today", size: "0.9 MB" }
  ];

  // Sidebar state
  const [filterOpen, setFilterOpen] = useState(true);
  const [customersOpen, setCustomersOpen] = useState(true);
  const [businessOpen, setBusinessOpen] = useState(true);
  const [individualsOpen, setIndividualsOpen] = useState(true);
  const [openCustomers, setOpenCustomers] = useState({});
  const [openTaxYears, setOpenTaxYears] = useState({});

  const toggleCustomer = (name) => {
    setOpenCustomers((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };
  const toggleTaxYear = (customerName, year) => {
    const key = `${customerName}_${year}`;
    setOpenTaxYears((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Dark mode logic
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return (
        window.localStorage.getItem("theme") === "dark" ||
        (!window.localStorage.getItem("theme") &&
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      );
    }
    return false;
  });
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark");
      window.localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark");
      window.localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // AI chat logic
  const [aiOpen, setAiOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hi! What bookkeeping task can I help you with?", fromUser: false }
  ]);
  const aiContainerRef = useRef(null);
  const [aiInput, setAiInput] = useState("");
  function handleAiSend(e) {
    e.preventDefault();
    if (aiInput.trim()) {
      setMessages((prev) => [...prev, { text: aiInput.trim(), fromUser: true }]);
      setAiInput("");
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { text: "I'm here to help! (AI response placeholder)", fromUser: false }
        ]);
      }, 500);
    }
  }

  // Customer Management (add/invite/remove)
  const handleAddCustomer = () => alert("Add customer flow (not implemented)");
  const handleInviteCustomer = () => alert("Invite customer flow (not implemented)");
  const handleRemoveCustomer = (name) => alert(`Remove ${name} (not implemented)`);

  // Multi-file upload logic
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  function handleFileChange(e) {
    let files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  }

  function handleDrop(e) {
    e.preventDefault();
    let files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function removeSelectedFile(index) {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }

  function handleUpload() {
    alert("Files would be uploaded here!");
    setSelectedFiles([]);
  }

  return (
    <div className="bs-section-dragged">
      <div className={`flex h-screen ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}>
        {/* Sidebar */}
        <div className={`w-64 h-full overflow-y-auto shadow-lg ${darkMode ? "bg-gray-800" : "bg-white"}`}>
          <div className="p-4 border-b dark:border-gray-700">
            <h1 className={`text-xl font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Bookkeeper Dashboard</h1>
          </div>
          <div className="px-4 py-2">
            <div className="p-4 border-b dark:border-gray-700">
              <div className="flex justify-between items-center mb-1">
                <h2 className={`text-md font-bold ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Customer Filter</h2>
                <button
                  onClick={() => setFilterOpen((open) => !open)}
                  className="focus:outline-none"
                  aria-label={filterOpen ? "Collapse" : "Expand"}
                >
                  <FontAwesomeIcon icon={filterOpen ? faChevronUp : faChevronDown} className={darkMode ? "text-gray-300" : "text-gray-600"} />
                </button>
              </div>
              {filterOpen && (
                <div className="space-y-3 mt-2">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Customer Type</label>
                    <select className={`w-full px-2 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                      ${darkMode ? "bg-gray-900 text-gray-200 border-gray-700" : "bg-white text-gray-800 border-gray-300"}`}>
                      <option>All Customers</option>
                      <option>Business</option>
                      <option>Individual</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Search</label>
                    <input
                      type="text"
                      placeholder="Search customers..."
                      className={`w-full px-2 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                        ${darkMode ? "bg-gray-900 text-gray-200 border-gray-700" : "bg-white text-gray-800 border-gray-300"}`}
                    />
                  </div>
                  <div className="pt-2">
                    <button className="w-full px-3 py-1.5 text-xs text-center font-medium bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-200 rounded-md text-white">
                      Apply Filters
                    </button>
                    <button className="w-full px-3 py-1.5 mt-2 text-xs text-center font-medium rounded-md
                      bg-gray-200 hover:bg-gray-300 text-gray-700
                      dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300
                      focus:ring-2 focus:ring-gray-100">
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Customers Section (collapsible, with Business/Individuals) */}
            <div className="mb-4">
              <div
                className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                onClick={() => setCustomersOpen((open) => !open)}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-600" />
                  <span className={`font-medium ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Customers</span>
                </div>
                <FontAwesomeIcon icon={customersOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
              </div>
              {customersOpen && (
                <div className="ml-2 mt-1">
                  {/* Business subfolder */}
                  <div className="cursor-pointer">
                    <div
                      className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => setBusinessOpen((open) => !open)}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-500" />
                        <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>Business</span>
                      </div>
                      <FontAwesomeIcon icon={businessOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
                    </div>
                    {businessOpen && (
                      <div className="ml-4">
                        {businessClients.map((client) => (
                          <div key={client.name}>
                            <div
                              className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              onClick={() => toggleCustomer(client.name)}
                            >
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{client.name}</span>
                              </div>
                              <FontAwesomeIcon icon={openCustomers[client.name] ? faChevronUp : faChevronDown} className="text-gray-500" />
                            </div>
                            {openCustomers[client.name] && (
                              <div className="ml-4">
                                {client.taxYears.map((taxYear) => {
                                  const yearKey = `${client.name}_${taxYear.year}`;
                                  return (
                                    <div key={taxYear.year}>
                                      <div
                                        className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                        onClick={() => toggleTaxYear(client.name, taxYear.year)}
                                      >
                                        <div className="flex items-center">
                                          <FontAwesomeIcon icon={faFolder} className="mr-2 text-green-500" />
                                          <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{taxYear.year}</span>
                                        </div>
                                        <FontAwesomeIcon icon={openTaxYears[yearKey] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                      </div>
                                      {openTaxYears[yearKey] && (
                                        <div className="ml-6">
                                          {expenseCategories
                                            .filter(cat => taxYear.expenses.includes(cat.label))
                                            .map(cat => (
                                              <div key={cat.label} className={`flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                                <FontAwesomeIcon icon={faIconsMap[cat.icon]} className="mr-2 text-gray-500" />
                                                <span className={`text-xs ${darkMode ? "text-gray-200" : "text-gray-700"}`}>{cat.label}</span>
                                              </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Individuals subfolder */}
                  <div className="cursor-pointer">
                    <div
                      className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 mt-2"
                      onClick={() => setIndividualsOpen((open) => !open)}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-500" />
                        <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>Individuals</span>
                      </div>
                      <FontAwesomeIcon icon={individualsOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
                    </div>
                    {individualsOpen && (
                      <div className="ml-4">
                        {individualClients.map((client) => (
                          <div key={client.name}>
                            <div
                              className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              onClick={() => toggleCustomer(client.name)}
                            >
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{client.name}</span>
                              </div>
                              <FontAwesomeIcon icon={openCustomers[client.name] ? faChevronUp : faChevronDown} className="text-gray-500" />
                            </div>
                            {openCustomers[client.name] && (
                              <div className="ml-4">
                                {client.taxYears.map((taxYear) => {
                                  const yearKey = `${client.name}_${taxYear.year}`;
                                  return (
                                    <div key={taxYear.year}>
                                      <div
                                        className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                        onClick={() => toggleTaxYear(client.name, taxYear.year)}
                                      >
                                        <div className="flex items-center">
                                          <FontAwesomeIcon icon={faFolder} className="mr-2 text-green-500" />
                                          <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{taxYear.year}</span>
                                        </div>
                                        <FontAwesomeIcon icon={openTaxYears[yearKey] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                      </div>
                                      {openTaxYears[yearKey] && (
                                        <div className="ml-6">
                                          {expenseCategories
                                            .filter(cat => taxYear.expenses.includes(cat.label))
                                            .map(cat => (
                                              <div key={cat.label} className={`flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                                <FontAwesomeIcon icon={faIconsMap[cat.icon]} className="mr-2 text-gray-500" />
                                                <span className={`text-xs ${darkMode ? "text-gray-200" : "text-gray-700"}`}>{cat.label}</span>
                                              </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Navigation */}
          <header className={`shadow-sm p-4 flex items-center justify-between ${darkMode ? "bg-gray-800" : "bg-white"}`}>
            <h1 className={`text-xl font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Overview</h1>
            <div className="flex items-center space-x-4">
              {/* Dark/Light Mode Toggle */}
              <button
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                onClick={() => setDarkMode((prev) => !prev)}
                aria-label="Toggle Dark Mode"
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                <FontAwesomeIcon
                  icon={darkMode ? faSun : faMoon}
                  className={darkMode ? "text-yellow-300" : "text-gray-700"}
                />
              </button>
              {/* Notification */}
              <div className="relative">
                <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative">
                  <FontAwesomeIcon icon={faBell} className={darkMode ? "text-gray-200" : "text-gray-600"} />
                  <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">1</span>
                </button>
              </div>
              {/* Messages */}
              <div className="relative">
                <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative">
                  <FontAwesomeIcon icon={faComment} className={darkMode ? "text-gray-200" : "text-gray-600"} />
                  <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">2</span>
                </button>
              </div>
              {/* User profile - at top right */}
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full border-2 ${darkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-100"} flex items-center justify-center`}>
                  <FontAwesomeIcon icon={faUser} className={`${darkMode ? "text-gray-200" : "text-gray-500"} text-xl`} />
                </div>
                <div className="ml-3 text-right">
                  <p className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Ava Lee, Bookkeeper</p>
                  <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Staff Bookkeeper</p>
                </div>
              </div>
            </div>
          </header>
          <main className={`flex-1 overflow-y-auto p-4 ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
            {/* Customer Management Section */}
            <div className={`rounded-lg shadow-md p-6 mb-6 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Customer Management</h2>
                <div className="flex space-x-2">
                  <button onClick={handleAddCustomer}
                    className="px-3 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-xs flex items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Customer
                  </button>
                  <button onClick={handleInviteCustomer}
                    className="px-3 py-2 rounded-md bg-green-500 hover:bg-green-600 text-white text-xs flex items-center"
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="mr-1" /> Invite
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className={`py-2 px-3 ${darkMode ? "text-gray-200" : "text-gray-700"} text-left`}>Customer</th>
                      <th className={`py-2 px-3 ${darkMode ? "text-gray-200" : "text-gray-700"} text-left`}>Contact</th>
                      <th className={`py-2 px-3 ${darkMode ? "text-gray-200" : "text-gray-700"} text-left`}>Assigned Since</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...businessClients, ...individualClients].map(c => (
                      <tr key={c.name} className={darkMode ? "border-b border-gray-700" : "border-b border-gray-200"}>
                        <td className={`py-2 px-3 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{c.name}</td>
                        <td className={`py-2 px-3 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{c.contact}</td>
                        <td className={`py-2 px-3 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{(c.taxYears && c.taxYears[0]?.year) || ""}</td>
                        <td className="py-2 px-3">
                          <button onClick={() => handleRemoveCustomer(c.name)}
                            className="px-2 py-1 rounded-md bg-red-500 hover:bg-red-600 text-white text-xs flex items-center"
                          >
                            <FontAwesomeIcon icon={faUserMinus} className="mr-1" /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* File Upload Section (Multi-file Select/Preview) */}
            <div className={`rounded-lg shadow-md p-6 mb-6 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
              <h2 className={`text-lg font-bold mb-4 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Document Upload</h2>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-150 cursor-pointer ${darkMode ? "border-gray-600 bg-gray-900 hover:bg-gray-800" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current.click()}
              >
                <FontAwesomeIcon icon={faCloudUploadAlt} className={`text-4xl mb-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
                <p className={`mb-2 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Drag and drop files here or</p>
                <button
                  type="button"
                  className="inline-block px-6 py-2.5 text-sm text-center text-white font-bold bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 rounded-full"
                  onClick={e => {
                    e.stopPropagation();
                    fileInputRef.current.click();
                  }}
                >
                  Browse Files
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-5 text-left">
                    <p className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Selected Files:</p>
                    <ul>
                      {selectedFiles.map((file, idx) => (
                        <li key={idx} className="flex items-center mb-2">
                          <FontAwesomeIcon
                            icon={file.type.includes("pdf") ? faFilePdf : faFileExcel}
                            className={file.type.includes("pdf") ? "text-red-500 mr-2" : "text-green-500 mr-2"}
                          />
                          <span className={`mr-3 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                          <button
                            className="ml-2 px-2 py-1 text-xs text-white bg-red-500 hover:bg-red-600 rounded"
                            onClick={e => {
                              e.stopPropagation();
                              removeSelectedFile(idx);
                            }}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button
                      className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpload();
                      }}
                    >
                      Upload Files
                    </button>
                  </div>
                )}
                <p className={`text-xs mt-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Supported formats: PDF, XLSX, CSV, JPG (Max size: 20MB)</p>
              </div>
              <div className="mt-4">
                <h3 className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Recent Uploads</h3>
                <div className="space-y-2">
                  {uploadedFiles.map((file, idx) => (
                    <div key={file.name} className={`flex items-center justify-between p-3 rounded-md ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={file.type === "pdf" ? faFilePdf : faFileExcel}
                          className={file.type === "pdf" ? "text-red-500 mr-3" : "text-green-500 mr-3"}
                        />
                        <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{file.name}</span>
                      </div>
                      <div className="flex items-center">
                        <span className={`text-xs mr-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{file.size} • {file.date}</span>
                        <button className={`${darkMode ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}>
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
        {/* Floating AI Assistant */}
        <div className="fixed bottom-6 right-6 z-50">
          {/* Ask AI Button */}
          <button
            className="flex items-center px-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg transition-all hover:shadow-xl"
            onClick={() => setAiOpen(true)}
          >
            <FontAwesomeIcon icon={faRobot} className="text-white mr-2" />
            <span className="text-white font-medium">Ask AI</span>
          </button>
          {/* AI Chat Floating Window */}
          {aiOpen && (
            <div
              ref={aiContainerRef}
              className="absolute bottom-16 right-0 w-80 max-w-sm bg-white dark:bg-gray-900 rounded-lg shadow-xl flex flex-col"
              style={{ height: 420 }}
            >
              {/* Chat Header */}
              <div className="flex justify-between items-center border-b pb-2 pt-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faRobot} className="w-7 h-7 text-blue-500 mr-2" />
                  <h3 className="font-bold text-gray-800 dark:text-gray-100">AI Assistant</h3>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setAiOpen(false)}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto mb-2 px-3 pt-2 pb-1 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex mb-2 ${msg.fromUser ? "justify-end" : ""}`}>
                    {!msg.fromUser && (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0 mr-2">
                        <FontAwesomeIcon icon={faRobot} className="text-xs" />
                      </div>
                    )}
                    <div className={`rounded-lg p-2 max-w-[75%] text-sm ${msg.fromUser ? "bg-blue-600 text-white" : "bg-blue-100 dark:bg-gray-800 dark:text-gray-100 text-gray-800"}`}>
                      {msg.text}
                    </div>
                    {msg.fromUser && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-blue-500 flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faUserCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Chat Input */}
              <form className="flex p-3 border-t border-gray-200 dark:border-gray-700" onSubmit={handleAiSend}>
                <input
                  type="text"
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  placeholder="Ask a question..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                />
                <button
                  type="submit"
                  className="px-3 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
